import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Sliders, ShieldCheck, MonitorDown, Database as DatabaseIcon } from "lucide-react";
import ProviderSettings from "@/components/ProviderSettings";
import AccountCard from "@/components/AccountCard";
import TeamPanel from "@/components/TeamPanel";
import EmailSettings from "@/components/EmailSettings";
import KitAccess from "@/components/KitAccess";
import { cn } from "@/lib/utils";

export default function SettingsPanel() {
  const [crawler, setCrawler] = useState({ per_domain_delay_ms: 1000, concurrency: 4, respect_robots: true });
  const [dbInfo, setDbInfo] = useState(null);

  useEffect(() => {
    api.getCrawlerSettings().then(setCrawler).catch(() => {});
    api.getDatabaseInfo().then(setDbInfo).catch(() => {});
  }, []);

  const setC = (k, v) => setCrawler((s) => ({ ...s, [k]: v }));

  const saveCrawler = async () => {
    await api.saveCrawlerSettings({
      per_domain_delay_ms: Number(crawler.per_domain_delay_ms),
      concurrency: Number(crawler.concurrency),
      respect_robots: crawler.respect_robots,
    });
    toast.success("Crawler defaults saved");
  };


  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-[900px]">
      <header>
        <h2 className="font-mono font-extrabold text-2xl sm:text-3xl tracking-tight uppercase">Settings</h2>
        <p className="text-sm text-slate-500 mt-1">Your account, AI provider keys, crawler defaults and storage.</p>
      </header>

      <AccountCard />

      <TeamPanel />

      <EmailSettings />

      <KitAccess />

      <ProviderSettings />

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <h3 className="font-display font-bold text-base">Crawler Politeness Defaults</h3>
        </div>
        <p className="text-xs text-slate-500 -mt-2">Applied to every new crawl job. Each job can override these in the Control Room.</p>

        <div className="flex items-center justify-between rounded-md border border-border bg-[#0E1420] px-4 py-3">
          <div>
            <Label className="text-sm text-slate-300">Respect robots.txt</Label>
            <p className="text-[11px] text-slate-600 font-mono mt-0.5">Skips disallowed paths and honours Crawl-delay directives</p>
          </div>
          <Switch data-testid="settings-respect-robots-switch" checked={crawler.respect_robots}
            onCheckedChange={(v) => setC("respect_robots", v)} />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Per-domain delay (ms)</Label>
            <Input data-testid="settings-domain-delay-input" type="number" min={0} step={100}
              value={crawler.per_domain_delay_ms} onChange={(e) => setC("per_domain_delay_ms", e.target.value)}
              className="bg-[#0E1420] border-border font-mono text-sm" />
            <p className="text-[11px] text-slate-600 font-mono">1000ms = polite. Enforced globally per hostname.</p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-slate-400">Concurrent fetchers per job</Label>
            <Input data-testid="settings-concurrency-input" type="number" min={1} max={16}
              value={crawler.concurrency} onChange={(e) => setC("concurrency", e.target.value)}
              className="bg-[#0E1420] border-border font-mono text-sm" />
            <p className="text-[11px] text-slate-600 font-mono">1-16. Multiple jobs can run at the same time.</p>
          </div>
        </div>

        <Button data-testid="crawler-settings-save-button" onClick={saveCrawler}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
          Save Crawler Defaults
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-3" data-testid="database-card">
        <div className="flex items-center gap-2">
          <DatabaseIcon className="h-4 w-4 text-emerald-400" />
          <h3 className="font-display font-bold text-base">Database</h3>
          {dbInfo && (
            <span className={cn("ml-auto px-2 py-0.5 rounded font-mono text-[10px] uppercase border",
              dbInfo.is_local ? "border-slate-600 bg-slate-700/20 text-slate-400"
                              : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300")}>
              {dbInfo.is_local ? "local mongodb" : "external mongodb"}
            </span>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-md border border-border bg-[#0E1420] px-3 py-2">
            <p className="text-[9px] font-mono uppercase tracking-wider text-slate-600">Host</p>
            <p className="font-mono text-sm text-slate-200 mt-0.5 truncate">{dbInfo?.host || "—"}</p>
          </div>
          <div className="rounded-md border border-border bg-[#0E1420] px-3 py-2">
            <p className="text-[9px] font-mono uppercase tracking-wider text-slate-600">Database name</p>
            <p className="font-mono text-sm text-slate-200 mt-0.5 truncate">{dbInfo?.db_name || "—"}</p>
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          Want to use your own MongoDB (self-hosted or Atlas)? Point{" "}
          <span className="font-mono text-slate-400">MONGO_URL</span> and{" "}
          <span className="font-mono text-slate-400">DB_NAME</span> in{" "}
          <span className="font-mono text-slate-400">backend/.env</span> at your cluster and restart the backend — every
          collection below is created automatically, nothing else changes.
        </p>
        <pre data-testid="database-collections"
          className="rounded-md border border-border bg-[#04060A] p-3 font-mono text-[11px] text-emerald-300/90 overflow-x-auto">{`MONGO_URL="mongodb+srv://user:pass@cluster.mongodb.net"
DB_NAME="dataforge"

collections: ${(dbInfo?.collections || []).join(", ")}`}</pre>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6 space-y-3">
        <div className="flex items-center gap-2">
          <MonitorDown className="h-4 w-4 text-purple-400" />
          <h3 className="font-display font-bold text-base">Desktop App</h3>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          A hosted browser cannot reach <span className="font-mono text-slate-400">localhost:11434</span> on your machine.
          Build the Electron desktop shell in <span className="font-mono text-slate-400">/app/desktop</span> to run DataForge AI
          as a native window that talks straight to your local Ollama.
        </p>
        <pre data-testid="desktop-build-instructions"
          className="rounded-md border border-border bg-[#04060A] p-3 font-mono text-[11px] text-cyan-300 overflow-x-auto">{`cd desktop
npm install
npm start                 # run the desktop app
npm run dist              # build installers (dmg / exe / AppImage)`}</pre>
        <p className="text-[11px] text-slate-600 font-mono">
          See desktop/README.md for the fully-local setup (bundled Python backend + MongoDB).
        </p>
      </div>

      <div className="rounded-lg border border-border bg-[#0B0F18] p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sliders className="h-4 w-4 text-slate-500" />
          <h3 className="font-display font-bold text-base text-slate-300">About</h3>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          DataForge AI crawls the web from your keywords and seed URLs, extracts clean text, filters by relevance, and keeps
          going until you stop it or the time limit is reached. Pause, resume and extend jobs without losing pages, schedule
          nightly runs, grade the corpus, then export shaped JSON/JSONL/CSV/TXT files to the Files vault.
        </p>
      </div>
    </div>
  );
}
